import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useApiQuery } from './useApiQuery';
import {
  getUnreadCount,
  listNotifications,
  type NotificationFilters,
} from '@/api/notifications';
import {
  mockNotifications,
  mockUnreadCount,
} from '@/data/mock-notifications-data';

const POLL_INTERVAL_MS = 30_000;

export const useUnreadCount = () => {
  const { data, isLoading, reload } = useApiQuery(
    {
      queryFn: () => getUnreadCount(),
      mockData: { count: mockUnreadCount },
    },
    [],
  );

  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      reload();
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current !== undefined) {
        clearInterval(intervalRef.current);
      }
    };
  }, [reload]);

  return {
    count: data?.count ?? 0,
    isLoading,
    reload,
  };
};

export const useNotifications = (filters?: NotificationFilters) => {
  const stableFilters = useMemo(() => filters, [
    filters?.page,
    filters?.limit,
    filters?.unreadOnly,
  ]);

  const fetchNotifications = useCallback(
    () => listNotifications(stableFilters ?? {}),
    [stableFilters],
  );

  const { data, isLoading, error, reload, isUsingMockData } = useApiQuery(
    {
      queryFn: fetchNotifications,
      mockData: { notifications: mockNotifications, total: mockNotifications.length },
    },
    [stableFilters],
  );

  return {
    notifications: data?.notifications ?? [],
    total: data?.total ?? 0,
    isLoading,
    error,
    reload,
    isUsingMockData,
  };
};
